// Spec 48 ai-ux — show_query_panel.
//
// "Collapsible code panel revealing the underlying query the assistant
// generated. Syntax-highlighted in code_md, with scope predicates
// commented for audit clarity. Copy action preserves the scope comment
// so pasting downstream retains context." (spec 48 line 725)
//
// a11y per spec 48:
//   - Toggle uses aria-expanded on the button; content has a unique id
//     referenced by aria-controls
//   - Code block has role="region" and aria-label="Generated query"
//   - Copy action uses aria-live="polite" for "Copied" feedback

import {
  forwardRef,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type HTMLAttributes,
} from "react";

import { cx } from "../lib/cx.ts";

export type QueryLanguage = "SQL" | "GraphQL" | "HRQL" | string;

// Omit the standard `onCopy` ClipboardEventHandler because we override
// the prop name with an app-level handler (text-only signature, not a
// ClipboardEvent).
export interface ShowQueryPanelProps extends Omit<HTMLAttributes<HTMLDivElement>, "onCopy"> {
  /** The generated query body (with scope-comment lines included). */
  readonly queryText: string;
  /** Language label shown in the panel header (drives syntax-highlight class). */
  readonly language: QueryLanguage;
  /** Initial expanded state; defaults to false. */
  readonly defaultExpanded?: boolean;
  /** Override "Show query" toggle label. */
  readonly showLabel?: string;
  /** Override "Hide query" toggle label. */
  readonly hideLabel?: string;
  /** Override "Copy" action label. */
  readonly copyLabel?: string;
  /** Override "Copied" feedback label. */
  readonly copiedLabel?: string;
  /** Override panel aria-label. */
  readonly queryAriaLabel?: string;
  /** Optional copy handler — defaults to navigator.clipboard.writeText (where available). */
  readonly onCopy?: (text: string) => void | Promise<void>;
}

export const ShowQueryPanel = forwardRef<HTMLDivElement, ShowQueryPanelProps>(
  (
    {
      queryText,
      language,
      defaultExpanded = false,
      showLabel = "Show query",
      hideLabel = "Hide query",
      copyLabel = "Copy",
      copiedLabel = "Copied",
      queryAriaLabel = "Generated query",
      onCopy,
      className,
      ...rest
    },
    ref,
  ) => {
    const [expanded, setExpanded] = useState(defaultExpanded);
    const [copied, setCopied] = useState(false);
    const panelId = useId();

    // Track the "Copied" feedback timer so we can:
    //   1. clear a pending timer if the user clicks Copy again (otherwise
    //      the earlier 2s setter would race against the new one and flip
    //      the feedback off prematurely);
    //   2. clear on unmount (otherwise setCopied fires on an unmounted
    //      component — React dev-mode warning + memory leak).
    const copyFeedbackTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
      return () => {
        if (copyFeedbackTimerRef.current !== null) {
          clearTimeout(copyFeedbackTimerRef.current);
          copyFeedbackTimerRef.current = null;
        }
      };
    }, []);

    const handleCopy = useCallback(async () => {
      try {
        if (onCopy !== undefined) {
          await onCopy(queryText);
        } else if (typeof navigator !== "undefined" && navigator.clipboard?.writeText !== undefined) {
          await navigator.clipboard.writeText(queryText);
        }
        setCopied(true);
        // 2-second feedback per spec line 734.
        if (copyFeedbackTimerRef.current !== null) {
          clearTimeout(copyFeedbackTimerRef.current);
        }
        copyFeedbackTimerRef.current = setTimeout(() => {
          setCopied(false);
          copyFeedbackTimerRef.current = null;
        }, 2000);
      } catch {
        // Silent failure — clipboard rejection (e.g. secure-context) is
        // a permission boundary, not a UI bug. Caller can override onCopy
        // to add their own toast.
      }
    }, [onCopy, queryText]);

    return (
      <div
        ref={ref}
        data-component="show-query-panel"
        className={cx("nai-show-query-panel", className)}
        {...rest}
      >
        <button
          type="button"
          data-component="show-query-toggle"
          className="nai-show-query-toggle"
          aria-expanded={expanded}
          aria-controls={panelId}
          onClick={() => setExpanded((e) => !e)}
        >
          {expanded ? hideLabel : showLabel}
        </button>
        {expanded && (
          <div
            id={panelId}
            role="region"
            aria-label={queryAriaLabel}
            data-component="show-query-content"
            data-language={language}
            className="nai-show-query-content"
          >
            <div className="nai-show-query-header">
              <span data-component="show-query-language" className="nai-show-query-language">
                {language}
              </span>
              <button
                type="button"
                data-component="show-query-copy"
                className="nai-show-query-copy"
                onClick={handleCopy}
              >
                {copyLabel}
              </button>
              <span
                aria-live="polite"
                data-component="show-query-copy-feedback"
                className="nai-show-query-copy-feedback"
              >
                {copied ? copiedLabel : ""}
              </span>
            </div>
            <pre data-component="show-query-code" className="nai-show-query-code">
              <code>{queryText}</code>
            </pre>
          </div>
        )}
      </div>
    );
  },
);
ShowQueryPanel.displayName = "ShowQueryPanel";
